import React, { useState } from 'react';
import {
  TickOneIcon,
  OneFacebookIcon,
  OneInstagramIcon,
  OneTikTokIcon,
} from '../../atoms/icons';
import ImageSlider from './ImageSlider ';
import VideoSlider from './videoSlider';
import { redirectToProductDetails } from '../../../utils';
import NoImage from '../../../assets/images/no-image.png';
import defaultAvatar from '../../../assets/images/default-avatar.jpg';

export const TemplateOne = ({
  expertiseData,
  preferredBrands,
  preferredProducts,
  redirectToBuyerPortal,
  redirectToShopPortal,
  cardDetails,
  schedulerEnabled,
}) => {
  const { seller, storefront } = cardDetails?.current?.config;
  const LogoUrl =
    storefront?.general?.longLogoImage?.url ||
    storefront?.general?.logoImage?.url ||
    null;

  const [isExpanded, setIsExpanded] = useState(false);
  const toggleReadMore = () => setIsExpanded(!isExpanded);

  const shopUrl = storefront?.general?.shopUrl;
  const profile = seller?.profile;

  const products = preferredProducts?.length
    ? {
        items: preferredProducts,
        label: 'Favorite Products',
        type: 'products',
      }
    : preferredBrands?.length
    ? { items: preferredBrands, label: 'Favorite Brands', type: 'brands' }
    : null;

  return (
    <div className="one-theme">
      {/* ── Hero ── */}
      <section className="one_header" style={{ paddingBottom: '3.5rem' }}>
        <div className="wrapper-one">
          {/* Logo */}
          <header className="flex justify-between items-center gap-6">
            {LogoUrl ? (
              <div className="logo-box max-w-56">
                <img src={LogoUrl} alt="logo" />
              </div>
            ) : (
              <h2 className="logo-text capitalize text-onePrimary">
                {storefront?.general?.name}
              </h2>
            )}
          </header>

          {/* Profile + About */}
          <section className="flex gap-8 justify-between md:flex-row flex-col-reverse text-center md:text-left items-center">
            <div style={{ maxWidth: '600px' }}>
              {profile?.title && (
                <h1
                  className="font-bold md:text-5xl text-3xl font-tempOne capitalize text-white-main break-words"
                  style={{ lineHeight: 1.15 }}
                >
                  {profile.title}
                </h1>
              )}

              {profile?.about && (
                <div className="mt-6">
                  <div
                    className={
                      isExpanded
                        ? ''
                        : profile.about.length > 600
                        ? 'clamped-text'
                        : ''
                    }
                  >
                    <p
                      className="font-normal text-lg font-tempOne leading-relaxed"
                      style={{ color: 'rgba(255,255,255,0.85)' }}
                      dangerouslySetInnerHTML={{ __html: profile.about }}
                    />
                  </div>
                  {profile.about.length > 600 && (
                    <button
                      className="text-onePrimary mt-2 font-semibold text-sm"
                      style={{ opacity: 0.9 }}
                      onClick={toggleReadMore}
                    >
                      {isExpanded ? 'Read Less' : 'Read More'}
                    </button>
                  )}
                </div>
              )}

              {/* Social icons */}
              <div className="flex gap-5 md:justify-start justify-center items-center mt-6">
                {profile?.facebookUrl && (
                  <a
                    href={profile.facebookUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cursor-pointer"
                  >
                    <OneFacebookIcon className="text-onePrimary" />
                  </a>
                )}
                {profile?.instagramUrl && (
                  <a
                    href={profile.instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cursor-pointer"
                  >
                    <OneInstagramIcon className="text-onePrimary" />
                  </a>
                )}
                {profile?.tiktokUrl && (
                  <a
                    href={profile.tiktokUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cursor-pointer"
                  >
                    <OneTikTokIcon className="text-onePrimary" />
                  </a>
                )}
              </div>

              {/* Shop the Store CTA — in hero */}
              <button
                className="text-white-main font-semibold text-lg mt-8 px-8 py-4 rounded-oneCustom bg-onePrimary border border-white-main"
                style={{ letterSpacing: '0.02em', transition: 'opacity .2s' }}
                onClick={() => redirectToShopPortal(shopUrl)}
              >
                Shop the Store
              </button>
            </div>

            {/* Avatar */}
            <div className="flex-shrink-0">
              <div className="image-wrapper">
                <img
                  src={
                    profile?.avatar?.mediumUrl ||
                    profile?.avatar?.url ||
                    defaultAvatar
                  }
                  alt="profile"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = defaultAvatar;
                  }}
                />
              </div>
            </div>
          </section>
        </div>
      </section>

      {/* ── Services / Expertise (near Book Appointment) ── */}
      {(expertiseData?.length > 0 || profile?.expertise) && (
        <section
          className="bg-white-main"
          style={{
            padding: '2.5rem 0 0',
            marginTop: '-1.25rem',
            borderRadius: '1.5rem 1.5rem 0 0',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <div
            className="wrapper-one"
            style={{ maxWidth: '1440px', margin: '0 auto', padding: '0 60px' }}
          >
            <h2
              className="font-semibold text-2xl font-tempOne mb-6 border-l-4 pl-4 border-primary"
              style={{ color: 'var(--color-primary)' }}
            >
              {expertiseData?.length > 0 ? 'Services' : 'Expertise'}
            </h2>

            {expertiseData?.length > 0 ? (
              <div
                className="grid gap-3"
                style={{
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                }}
              >
                {expertiseData.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 rounded-lg"
                    style={{
                      padding: '0.75rem 1rem',
                      background: 'var(--color-gray-very-light)',
                    }}
                  >
                    <TickOneIcon className="text-onePrimary min-20" />
                    <span className="font-normal text-base break-words text-black-main">
                      {item?.name}
                    </span>
                  </div>
                ))}
              </div>
            ) : profile?.expertise ? (
              <p
                className="font-tempOne text-base leading-relaxed"
                dangerouslySetInnerHTML={{ __html: profile.expertise }}
              />
            ) : null}
          </div>

          {/* Book appointment CTA — grouped with services */}
          {schedulerEnabled && (
            <div className="text-center mt-8">
              <button
                className="bg-onePrimary text-white-main font-semibold text-lg px-8 py-4 rounded-oneCustom border border-oneSecondary"
                style={{
                  letterSpacing: '0.02em',
                  transition: 'opacity .2s',
                }}
                onClick={() => redirectToBuyerPortal()}
              >
                Book an Appointment
              </button>
            </div>
          )}
        </section>
      )}

      {/* ── Products + Shop ── */}
      <section
        className="one-body"
        style={{
          paddingTop:
            expertiseData?.length > 0 || profile?.expertise
              ? '1.5rem'
              : undefined,
        }}
      >
        <div className="wrapper-one">
          {products && (
            <div className="text-center">
              <h2 className="text-black-main font-semibold text-2xl font-tempOne mt-4 mb-6">
                {products.label}
              </h2>
              <div className="flex md:gap-4 gap-3 flex-wrap justify-center">
                {products.items.slice(0, 4).map((product, index) => {
                  const imgSrc =
                    products.type === 'brands'
                      ? product?.coverImage?.mediumUrl ||
                        product?.coverImage?.thumbUrl ||
                        NoImage
                      : product?.primaryImage?.mediumUrl ||
                        product?.primaryImage?.thumbUrl ||
                        NoImage;
                  const slug =
                    products.type === 'brands'
                      ? shopUrl + '/brands/' + product?.slug
                      : shopUrl + '/products/' + product?.slug;
                  return (
                    <div
                      key={index}
                      className="image-wrp cursor-pointer"
                      style={{ borderRadius: '12px', overflow: 'hidden' }}
                      onClick={() => redirectToProductDetails(slug)}
                    >
                      <img
                        src={imgSrc}
                        alt={product?.name || `Product ${index + 1}`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Gallery ── */}
          {seller?.profile?.galleryImages?.length > 0 && (
            <div className="mt-10">
              <h2 className="text-black-main font-semibold text-2xl font-tempOne mb-5 md:text-left text-center">
                Gallery
              </h2>
              <ImageSlider images={seller.profile.galleryImages} />
            </div>
          )}

          {/* ── Videos ── */}
          {seller?.profile?.galleryVideoLinks?.length > 0 && (
            <div className="mt-10">
              <h2 className="text-black-main font-semibold text-2xl font-tempOne mb-5 md:text-left text-center">
                Videos
              </h2>
              <VideoSlider videos={seller.profile.galleryVideoLinks} />
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
